import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { EPOCH_COMMUNICATION_NOTIFICATION } from '../tabs/EditEventCommunications/types/communication.types';
import { ScheduleImpactCardProps } from '../components/ScheduleImpactCard/ScheduleImpactCard';

type BaseDialogState = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

type ResendFeedbackFormModalState = Omit<BaseDialogState, 'open'> & {
  onSuccess: (() => void) | null;
  feedbackRequest: EPOCH_COMMUNICATION_NOTIFICATION | null;
  open: (options?: {
    onSuccess?: () => void;
    feedbackRequest?: EPOCH_COMMUNICATION_NOTIFICATION;
  }) => void;
};

type AsyncInviteJobProcessingState = {
  polling: boolean;
  // Actions
  startPolling: () => void;
  stopPolling: () => void;
};

type SchedulePublishModalState = Omit<BaseDialogState, 'open'> & {
  isReschedule: boolean;
  open: (options?: { isReschedule?: boolean }) => void;
};

type PublishErrorsState = {
  errors: ScheduleImpactCardProps[];
  isBannerShown: boolean;
  isDetailsModalOpen: boolean;
  showBanner: (errors: ScheduleImpactCardProps[]) => void;
  dismissBanner: () => void;
  toggleErrorsDetailsModal: () => void;
};

type EventEditorState = {
  editDetailsSheet: BaseDialogState;
  inviteGuestsModal: BaseDialogState;
  deleteDraftEventModal: BaseDialogState;
  cancelEventModal: BaseDialogState;
  editSelfCheckinModal: BaseDialogState;
  editFeedbackSettingsModal: BaseDialogState;
  resendFeedbackFormModal: ResendFeedbackFormModalState;
  schedulePublishModal: SchedulePublishModalState;
  publishErrorsModal: PublishErrorsState;
  asyncInviteJobProcessing: AsyncInviteJobProcessingState;
  createDraftSessionSheet: BaseDialogState;
};

/**
 * This is a global state for the event editor. It's used to manage state of only global UI components (e.g. modals, sheets, etc.)
 *
 * This store should not be exported and used outside of this file. Individual sub-states should be exposed as hooks.
 */
const useEventEditorState = create<EventEditorState>()(
  immer((setState) => ({
    editDetailsSheet: {
      isOpen: false,
      open: (): void =>
        setState((draftState) => {
          draftState.editDetailsSheet.isOpen = true;
        }),
      close: (): void =>
        setState((draftState) => {
          draftState.editDetailsSheet.isOpen = false;
        })
    },
    inviteGuestsModal: {
      isOpen: false,
      open: (): void =>
        setState((draftState) => {
          draftState.inviteGuestsModal.isOpen = true;
        }),
      close: (): void =>
        setState((draftState) => {
          draftState.inviteGuestsModal.isOpen = false;
        })
    },
    deleteDraftEventModal: {
      isOpen: false,
      open: (): void => {
        setState((draftState) => {
          draftState.deleteDraftEventModal.isOpen = true;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.deleteDraftEventModal.isOpen = false;
        });
      }
    },
    cancelEventModal: {
      isOpen: false,
      open: (): void => {
        setState((draftState) => {
          draftState.cancelEventModal.isOpen = true;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.cancelEventModal.isOpen = false;
        });
      }
    },
    editSelfCheckinModal: {
      isOpen: false,
      open: (): void => {
        setState((draftState) => {
          draftState.editSelfCheckinModal.isOpen = true;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.editSelfCheckinModal.isOpen = false;
        });
      }
    },
    editFeedbackSettingsModal: {
      isOpen: false,
      open: (): void => {
        setState((draftState) => {
          draftState.editFeedbackSettingsModal.isOpen = true;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.editFeedbackSettingsModal.isOpen = false;
        });
      }
    },
    resendFeedbackFormModal: {
      isOpen: false,
      onSuccess: null,
      feedbackRequest: null,
      open: (options?: {
        onSuccess?: () => void;
        feedbackRequest?: EPOCH_COMMUNICATION_NOTIFICATION;
      }): void => {
        setState((draftState) => {
          draftState.resendFeedbackFormModal.isOpen = true;
          draftState.resendFeedbackFormModal.onSuccess = options?.onSuccess ?? null;
          draftState.resendFeedbackFormModal.feedbackRequest = options?.feedbackRequest ?? null;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.resendFeedbackFormModal.isOpen = false;
          draftState.resendFeedbackFormModal.onSuccess = null;
          draftState.resendFeedbackFormModal.feedbackRequest = null;
        });
      }
    },
    schedulePublishModal: {
      isOpen: false,
      isReschedule: false,
      open: (options?: { isReschedule?: boolean }): void => {
        setState((draftState) => {
          draftState.schedulePublishModal.isOpen = true;
          draftState.schedulePublishModal.isReschedule = options?.isReschedule ?? false;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.schedulePublishModal.isOpen = false;
          draftState.schedulePublishModal.isReschedule = false;
        });
      }
    },
    publishErrorsModal: {
      errors: [],
      isBannerShown: false,
      isDetailsModalOpen: false,
      showBanner: (errors: ScheduleImpactCardProps[]): void => {
        setState((draftState) => {
          draftState.publishErrorsModal.errors = errors;
          draftState.publishErrorsModal.isBannerShown = true;
        });
      },
      dismissBanner: (): void => {
        setState((draftState) => {
          draftState.publishErrorsModal.errors = [];
          draftState.publishErrorsModal.isBannerShown = false;
          draftState.publishErrorsModal.isDetailsModalOpen = false;
        });
      },
      toggleErrorsDetailsModal: (): void => {
        setState((draftState) => {
          draftState.publishErrorsModal.isDetailsModalOpen =
            !draftState.publishErrorsModal.isDetailsModalOpen;
        });
      }
    },
    asyncInviteJobProcessing: {
      polling: false,
      startPolling: (): void => {
        setState((draftState) => {
          draftState.asyncInviteJobProcessing.polling = true;
        });
      },
      stopPolling: (): void => {
        setState((draftState) => {
          draftState.asyncInviteJobProcessing.polling = false;
        });
      }
    },
    createDraftSessionSheet: {
      isOpen: false,
      open: (): void => {
        setState((draftState) => {
          draftState.createDraftSessionSheet.isOpen = true;
        });
      },
      close: (): void => {
        setState((draftState) => {
          draftState.createDraftSessionSheet.isOpen = false;
        });
      }
    }
  }))
);

const useEditEventDetailsSheet = (): EventEditorState['editDetailsSheet'] =>
  useEventEditorState((state) => state.editDetailsSheet);

const useInviteGuestsModal = (): EventEditorState['inviteGuestsModal'] =>
  useEventEditorState((state) => state.inviteGuestsModal);

const useDeleteDraftEventModal = (): EventEditorState['deleteDraftEventModal'] =>
  useEventEditorState((state) => state.deleteDraftEventModal);

const useCancelEventModal = (): EventEditorState['cancelEventModal'] =>
  useEventEditorState((state) => state.cancelEventModal);

const useEditSelfCheckinModal = (): EventEditorState['editSelfCheckinModal'] =>
  useEventEditorState((state) => state.editSelfCheckinModal);

const useEditFeedbackSettingsModal = (): EventEditorState['editFeedbackSettingsModal'] =>
  useEventEditorState((state) => state.editFeedbackSettingsModal);

const useResendFeedbackFormModal = (): EventEditorState['resendFeedbackFormModal'] =>
  useEventEditorState((state) => state.resendFeedbackFormModal);

const useSchedulePublishModal = (): EventEditorState['schedulePublishModal'] =>
  useEventEditorState((state) => state.schedulePublishModal);

const usePublishErrorsModal = (): EventEditorState['publishErrorsModal'] =>
  useEventEditorState((state) => state.publishErrorsModal);

const useAsyncInviteJobProcessing = (): EventEditorState['asyncInviteJobProcessing'] =>
  useEventEditorState((state) => state.asyncInviteJobProcessing);

const useCreateDraftSessionSheet = (): EventEditorState['createDraftSessionSheet'] =>
  useEventEditorState((state) => state.createDraftSessionSheet);

export {
  useEditEventDetailsSheet,
  useInviteGuestsModal,
  useDeleteDraftEventModal,
  useCancelEventModal,
  useAsyncInviteJobProcessing,
  useEditSelfCheckinModal,
  useEditFeedbackSettingsModal,
  useResendFeedbackFormModal,
  useSchedulePublishModal,
  usePublishErrorsModal,
  useCreateDraftSessionSheet
};
